import { InfoCircleFilled } from '@ant-design/icons';
import { Form, FormInstance } from 'antd';
import clsx from 'clsx';
import { useWatch } from 'rc-field-form';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NouveauDossierConcessionnaireData, RAI } from 'types/Concessionnaire';
import Step3MoraleCalculationSection from './step3/Step3MoraleCalculationSection';
import Step3MoraleRepartitionSection from './step3/Step3MoraleRepartitionSection';
import {
    buildStep3PersonneMoraleViewModel,
    clampBeneficiairePercent,
    normalizeBeneficiaireRepartition,
    normalizeSliderValue,
    SliderValue,
} from './step3/step3PersonneMorale.model';

type Props = {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  primeParGwh?: number;
  primeParVehicule?: number;
  volume?: number;
  rai?: RAI;
};

export default function Step3PersonneMorale({
  form,
  primeParVehicule,
  volume,
  rai,
}: Props) {
  const { t } = useTranslation();
  const [primeErrorMessage, setPrimeErrorMessage] = useState<string | null>(
    null,
  );

  const watchedRepartition = useWatch('repartition', form);
  const rawRepartition =
    watchedRepartition ?? form.getFieldValue('repartition');
  const repartition = normalizeBeneficiaireRepartition(rawRepartition);
  const nbVehicules = form.getFieldValue('nbVehicules') ?? 1;
  const categorieVehicule = form.getFieldValue('categorieVehicule');

  useEffect(() => {
    // Keep the submitted form value aligned with the split shown in the UI.
    if (rawRepartition === repartition) {
      return;
    }

    form.setFieldValue('repartition', repartition);
  }, [form, rawRepartition, repartition]);

  const {
    showSlider,
    shouldShowCompactResults,
    primeParVehiculeValue,
    volumeMwhc,
    totalVolumeMwhc,
    beneficiairePercent,
    primeBeneficiaire,
    primeConcessionnaire,
    totalPrimeBeneficiaire,
    totalPrimeConcessionnaire,
    totalPrime,
    maxPrimeConcessionnairePerVehicle,
    minPrimeBeneficiaire,
  } = buildStep3PersonneMoraleViewModel({
    rai,
    primeParVehicule,
    volume,
    nbVehicules,
    repartition,
  });

  const repartitionSummary = t(
    'calculer_une_prime:resultat:repartition_summary',
  );
  const [summaryBefore, summaryAfter] =
    repartitionSummary.split('{{pourcentage}}');
  const totalLabelKey = showSlider
    ? 'nouveau_formulaire:resultat:prime_globale'
    : 'nouveau_formulaire:resultat:prime_cee';

  const setBeneficiaireRepartition = useCallback(
    (nextPercent: number) => {
      form.setFieldValue('repartition', clampBeneficiairePercent(nextPercent));
    },
    [form],
  );

  const handleConcessionnaireInputChange = useCallback(
    (nextValue: number | null) => {
      if (!primeParVehiculeValue || nextValue === null) {
        return;
      }

      const normalizedValue = Math.max(
        0,
        Math.min(primeParVehiculeValue, nextValue),
      );

      if (normalizedValue > maxPrimeConcessionnairePerVehicle) {
        setPrimeErrorMessage(
          t('nouveau_formulaire:resultat:montant_min_prime'),
        );
        return;
      }

      const nextBeneficiaryPercent =
        ((primeParVehiculeValue - normalizedValue) / primeParVehiculeValue) *
        100;

      setBeneficiaireRepartition(nextBeneficiaryPercent);
      setPrimeErrorMessage(null);
    },
    [
      maxPrimeConcessionnairePerVehicle,
      primeParVehiculeValue,
      setBeneficiaireRepartition,
      t,
    ],
  );

  const handleBeneficiaireInputChange = useCallback(
    (nextValue: number | null) => {
      if (!primeParVehiculeValue || nextValue === null) {
        return;
      }

      const normalizedValue = Math.max(
        0,
        Math.min(primeParVehiculeValue, nextValue),
      );

      if (normalizedValue < minPrimeBeneficiaire) {
        setPrimeErrorMessage(
          t('nouveau_formulaire:resultat:montant_min_prime'),
        );
        return;
      }

      const nextBeneficiaryPercent =
        (normalizedValue / primeParVehiculeValue) * 100;

      setBeneficiaireRepartition(nextBeneficiaryPercent);
      setPrimeErrorMessage(null);
    },
    [
      minPrimeBeneficiaire,
      primeParVehiculeValue,
      setBeneficiaireRepartition,
      t,
    ],
  );

  const handleConcessionnaireSliderChange = useCallback(
    (nextValue: SliderValue) => {
      handleConcessionnaireInputChange(normalizeSliderValue(nextValue));
    },
    [handleConcessionnaireInputChange],
  );

  const handleBeneficiaireSliderChange = useCallback(
    (nextValue: SliderValue) => {
      handleBeneficiaireInputChange(normalizeSliderValue(nextValue));
    },
    [handleBeneficiaireInputChange],
  );

  return (
    <Form
      form={form}
      name="step3"
      layout="vertical"
      className="[&_label]:!mb-0 [&_label]:font-semibold [&_.ant-form-item]:!mb-0"
    >
      <Form.Item<NouveauDossierConcessionnaireData>
        name="repartition"
        hidden
        initialValue={100}
      >
        <input />
      </Form.Item>

      <div
        className={
          shouldShowCompactResults ? 'w-full md:mx-auto md:max-w-[492px]' : ''
        }
      >
        {showSlider ? (
          <p className="-mt-4 mb-6 text-sm leading-5 text-gris-clair-hellio">
            {t('nouveau_formulaire:resultat:montant_de_la_prime_description')}
          </p>
        ) : (
          <div className="mb-6 rounded-lg bg-bleu-hellio-1 px-4 py-3">
            <p className="flex items-start gap-2 text-gris-hellio-6">
              <InfoCircleFilled className="mt-[4px] text-bleu-hellio-6" />
              <span>
                {t(
                  'nouveau_formulaire:resultat:montant_de_la_prime_description',
                )}
              </span>
            </p>
          </div>
        )}

        <div
          className={`flex flex-col gap-6 ${
            showSlider ? 'md:flex-row md:items-stretch md:gap-5' : ''
          }`}
        >
          {showSlider && (
            <div
              className={clsx(
                'md:basis-0 md:flex-1',
                showSlider && 'md:border-r md:border-color-border md:pr-5',
              )}
            >
              <Step3MoraleRepartitionSection
                primeParVehiculeValue={primeParVehiculeValue}
                primeConcessionnaire={primeConcessionnaire}
                primeBeneficiaire={primeBeneficiaire}
                beneficiairePercent={beneficiairePercent}
                summaryBefore={summaryBefore}
                summaryAfter={summaryAfter}
                primeErrorMessage={primeErrorMessage}
                onConcessionnaireInputChange={handleConcessionnaireInputChange}
                onBeneficiaireInputChange={handleBeneficiaireInputChange}
                onConcessionnaireSliderChange={
                  handleConcessionnaireSliderChange
                }
                onBeneficiaireSliderChange={handleBeneficiaireSliderChange}
              />
            </div>
          )}

          <div className={showSlider ? 'md:basis-0 md:flex-1' : ''}>
            <Step3MoraleCalculationSection
              showSlider={showSlider}
              categorieVehicule={categorieVehicule}
              volumeMwhc={volumeMwhc}
              totalVolumeMwhc={totalVolumeMwhc}
              nbVehicules={nbVehicules}
              primeBeneficiaire={primeBeneficiaire}
              totalPrimeBeneficiaire={totalPrimeBeneficiaire}
              totalPrimeConcessionnaire={totalPrimeConcessionnaire}
              totalPrime={totalPrime}
              totalLabelKey={totalLabelKey}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}
